import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import {
  Expression,
  ExpressionId,
  Form0,
  FormNode,
  FormStepId,
  FormStyleElement,
  Identity0,
  IdentityId,
  IdentityInformation,
  InputStepId,
  InputSubstep2,
  SectionStepId,
  Value0,
  ValueId,
} from '~types'

interface FormNodesState {
  formNodes: Record<FormStepId | SectionStepId | InputStepId, FormNode>
  expressions: Record<ExpressionId, Expression>
  expressionValues: Record<ValueId, Value0>
  formIdMap: Record<string, string>
  formCaseOwner: Identity0 | null
  formCaseRequester: IdentityId | null
  caseId: string | null
  identityFormData: Record<string, IdentityInformation> // key is frontend instance id
  completedSteps: Record<FormStepId, boolean> // Track which steps are completed
  formInReview: Form0 | null
  pageBreaks: Record<FormStepId, FormStyleElement[]>
  inputsFailingValidation: InputSubstep2[]
}

const initialState: FormNodesState = {
  formNodes: {},
  expressions: {},
  expressionValues: {},
  formIdMap: {},
  formCaseOwner: null,
  formCaseRequester: null,
  caseId: null,
  identityFormData: {},
  completedSteps: {},
  formInReview: null,
  pageBreaks: {},
  inputsFailingValidation: [],
}

const formNodesSlice = createSlice({
  name: 'formNodes',
  initialState,
  reducers: {
    setFormCaseOwner: (state, action: PayloadAction<{ formCaseOwner: Identity0 | null }>) => {
      state.formCaseOwner = action.payload.formCaseOwner
    },
    setFormCaseRequesterId: (state, action: PayloadAction<{ formCaseRequester: IdentityId }>) => {
      state.formCaseRequester = action.payload.formCaseRequester
    },
    setCaseId: (state, action: PayloadAction<{ caseId: string | null }>) => {
      state.caseId = action.payload.caseId
    },
    setFormNode: (
      state,
      action: PayloadAction<{
        nodeId: FormStepId | SectionStepId | InputStepId
        node: FormNode
      }>,
    ) => {
      state.formNodes[action.payload.nodeId] = action.payload.node
    },
    removeFormNode: (
      state,
      action: PayloadAction<{ nodeId: FormStepId | SectionStepId | InputStepId }>,
    ) => {
      delete state.formNodes[action.payload.nodeId]
    },
    setExpressions: (state, action: PayloadAction<{ expressions: Record<string, Expression> }>) => {
      state.expressions = action.payload.expressions
    },
    setExpressionValues: (
      state,
      action: PayloadAction<{ expressionValues: Record<string, Value0> }>,
    ) => {
      state.expressionValues = action.payload.expressionValues
    },
    setFormIdMap: (state, action: PayloadAction<{ formIdMap: Record<string, string> }>) => {
      state.formIdMap = action.payload.formIdMap
    },
    setIdentityData: (
      state,
      action: PayloadAction<{ instanceId: string; identityFormData: IdentityInformation }>,
    ) => {
      state.identityFormData[action.payload.instanceId] = action.payload.identityFormData
    },
    setStepCompletion: (
      state,
      action: PayloadAction<{
        stepId: FormStepId
        completed: boolean
      }>,
    ) => {
      state.completedSteps[action.payload.stepId] = action.payload.completed
    },
    clearFormNodes: (state) => {
      state.formNodes = {}
      state.expressions = {}
      state.expressionValues = {}
      state.formIdMap = {}
      state.completedSteps = {}
      state.caseId = null
    },
    clearFormCaseOwner: (state) => {
      state.formCaseOwner = null
    },
    clearFormCaseRequester: (state) => {
      state.formCaseRequester = null
    },
    setFormInReview: (state, action: PayloadAction<{ formInReview: Form0 | null }>) => {
      state.formInReview = action.payload.formInReview
    },
    setPageBreaks: (
      state,
      action: PayloadAction<{ stepId: FormStepId; pageBreaks: FormStyleElement[] }>,
    ) => {
      state.pageBreaks[action.payload.stepId] = action.payload.pageBreaks
    },
    setInputsFailingValidation: (
      state,
      action: PayloadAction<{ inputsFailingValidation: InputSubstep2[] }>,
    ) => {
      state.inputsFailingValidation = action.payload.inputsFailingValidation
    },
  },
})

export const {
  setFormCaseOwner,
  clearFormCaseOwner,
  setFormCaseRequesterId,
  clearFormCaseRequester,
  setCaseId,
  setFormNode,
  removeFormNode,
  setExpressions,
  setExpressionValues,
  setFormIdMap,
  setIdentityData,
  setStepCompletion,
  clearFormNodes,
  setFormInReview,
  setPageBreaks,
  setInputsFailingValidation,
} = formNodesSlice.actions

export default formNodesSlice.reducer
